import React from 'react';
import { useSelector } from 'react-redux';
import { IReduxState } from 'src/store/reducers';
import Box from '@mui/material/Box';
import { makeStyles, useMediaQuery, Typography } from '@material-ui/core';
import './index.css';
import QuoteProvider from './components/steps/Context/QuoteConfirmedContext';
import AppointmentStep from './components/steps/AppointmentStep';
import PaymentStep from './components/steps/PaymentStep';
import QuoteSummary from './components/atoms/QuoteSummary';
import Referral from './components/atoms/ReferralCode';
import InfoCard from './components/atoms/InfoCard';
import PayInFour from './components/atoms/PayInFour';
import EstimateStep from './components/steps/EstimateStep';
import SuccessPage from './components/steps/SuccessPage';

interface StepItem {
  id: number;
  label: string;
  image: JSX.Element;
}

interface QuoteServiceConfirmStepperProps {
  steps: StepItem[];
}

const useStyles = makeStyles({
  stepConnector: {
    width: '100%',
    '& span': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
      '& .Mui-active': {
        color: '#005959 !important',
      },
      '& .Mui-completed': {
        color: '#005959 !important',
      },
    },
  },
  boxStyle: {
    width: '100%',
    marginTop: 10,
  },
  customFontHead: {
    color: '#000',
    textAlign: 'center',
    fontFamily: 'Inter',

    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '30px',
    marginTop: 40,
  },
  customFontSub: {
    color: '#000',
    textAlign: 'center',
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '30px',
    marginTop: 10,
  },
  customStepperLabel: {
    color: '#005959',
  },
});

const QuoteServiceConfirmStepper: React.FC<QuoteServiceConfirmStepperProps> = ({
  steps,
}) => {
  const classes = useStyles();

  const appointment = useSelector(
    (state: IReduxState) => state.quote.appointment
  );

  const [activeStep, setActiveStep] = React.useState(0);
  const [completedSteps, setCompletedSteps] = React.useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
  ]);

  const isSm = useMediaQuery('(max-width: 1300px)');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepCompletion = () => {
    const updatedCompletedSteps = completedSteps.map((completed, index) =>
      index === activeStep ? true : completed
    );
    setCompletedSteps(updatedCompletedSteps);
  };

  const getStepComponent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <QuoteProvider>
            {!isSm && (
              <>
                <Typography
                  variant="body2"
                  className={classes.customFontHead}
                  style={{ fontSize: 20 }}
                >
                  Your Fixed Quote
                </Typography>

                <Typography className={classes.customFontSub}>
                  Get a Fixed Quote • Technician Comes To You • Pay at Time of
                  Service
                </Typography>
              </>
            )}
            {isSm && (
              <Typography
                className={classes.customFontHead}
                style={{ fontSize: 17 }}
              >
                Your Fixed Quote
              </Typography>
            )}
            <PayInFour />
            <div
              style={{
                display: !isSm ? 'flex' : 'unset',
                flexDirection: 'row',
              }}
            >
              <div
                style={{
                  marginLeft: !isSm ? 'auto' : 'none',
                  marginTop: !isSm ? 30 : 0,
                }}
              >
                <EstimateStep
                  onNext={handleNext}
                  onBack={handleBack}
                  bookingLink
                />
              </div>
              <div
                style={{
                  marginRight: !isSm ? 'auto' : 'none',
                  marginTop: 50,
                  marginLeft: 10,
                }}
              >
                {!isSm && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 'min-content',
                    }}
                  >
                    <QuoteSummary /> <InfoCard />
                    {!appointment?.attributes.shop_network && <Referral />}
                  </div>
                )}
              </div>
            </div>
          </QuoteProvider>
        );
      case 1:
        return (
          <QuoteProvider>
            {!isSm && (
              <>
                <Typography
                  variant="body2"
                  className={classes.customFontHead}
                  style={{ fontSize: 20 }}
                >
                  {appointment?.attributes.shop_network
                    ? 'Choose a time for your tow. We will be there.'
                    : 'Choose a time that suits you best. We will be there.'}
                </Typography>

                <Typography className={classes.customFontSub}>
                  Get a Fixed Quote • Technician Comes To You • Pay at Time of
                  Service
                </Typography>
              </>
            )}
            {isSm && (
              <Typography
                className={classes.customFontHead}
                style={{ fontSize: 17 }}
              >
                {appointment?.attributes.shop_network
                  ? 'Choose a time for your tow. We will be there.'
                  : 'Choose a time that suits you best. We will be there.'}
              </Typography>
            )}
            <PayInFour />
            <div
              style={{
                display: !isSm ? 'flex' : 'unset',
                flexDirection: 'row',
              }}
            >
              <div style={{ marginLeft: !isSm ? 'auto' : 'none' }}>
                <AppointmentStep
                  onNext={handleNext}
                  onBack={handleBack}
                  onComplete={handleStepCompletion}
                />
              </div>
              <div
                style={{
                  marginRight: !isSm ? 'auto' : 'none',
                  marginTop: 50,
                  marginLeft: 10,
                }}
              >
                {!isSm && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 'min-content',
                    }}
                  >
                    <QuoteSummary /> <InfoCard />
                    {!appointment?.attributes.shop_network && <Referral />}
                  </div>
                )}
              </div>
            </div>
          </QuoteProvider>
        );
      case 2:
        return (
          <QuoteProvider>
            <Typography
              className={classes.customFontHead}
              style={{ fontSize: isSm ? 17 : 20 }}
              variant="body2"
            >
              Securely Enter Payment Info
            </Typography>
            {!isSm && (
              <Typography className={classes.customFontSub}>
                Get a Fixed Quote • Technician Comes To You • Pay at Time of
                Service
              </Typography>
            )}
            <div
              style={{
                display: !isSm ? 'flex' : 'unset',
                flexDirection: 'row',
              }}
            >
              <div style={{ marginLeft: !isSm ? 'auto' : 'none' }}>
                <PaymentStep
                  onNext={handleNext}
                  onBack={handleBack}
                  onComplete={handleStepCompletion}
                  hideBack={false}
                />
              </div>
              <div
                style={{
                  marginRight: !isSm ? 'auto' : 'none',
                  marginTop: 50,
                  marginLeft: 10,
                }}
              >
                {!isSm && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 'min-content',
                    }}
                  >
                    <QuoteSummary /> <InfoCard />{' '}
                    {!appointment?.attributes.shop_network && <Referral />}
                  </div>
                )}
              </div>
            </div>
          </QuoteProvider>
        );
      case 3:
        return (
          <QuoteProvider>
            <SuccessPage />
          </QuoteProvider>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        marginTop: 10,
        paddingBottom: 20,
        maxWidth: '65%',
        minWidth: '50%',
        marginLeft: 'auto',
        marginRight: 'auto',
        '@media (max-width: 600px)': {
          minWidth: '100%',
        },
      }}
    >
      {getStepComponent(activeStep)}
    </Box>
  );
};

export default QuoteServiceConfirmStepper;
